<template>
  <div class="dashboard-page">
    <Panel
      hide-header
      class="call-logs-stats"
      data-cy="call-logs-stats"
    >
      <template #content>
        <div class="d-flex flex-wrap call-logs-stats__cards">
          <ReportingCardStats
            :icon="['fas', 'phone-volume']"
            :stats-number="totalItems"
            :stats-title="totalCallsTitle"
            :icon-color="ICON_COLORS.totalCalls"
          />

          <ReportingCardStats
            :icon="['fas', 'money-check-alt']"
            :stats-number="totalCost"
            is-stats-number-currency
            stats-title="Total Spent"
            :icon-color="ICON_COLORS.totalSpent"
            data-cy="card-total-spent"
          >
            <template #aside>
              <h6
                title="RPC, cost per call - Total Spent divided by Total Calls"
                class="card-title text-uppercase text-muted mb-1 card-ratio-title"
                data-cy="card-cost-per-call-title"
              >
                RPC
              </h6>
              <span
                class="h5 font-weight-bold mb-0"
                data-cy="card-cost-per-call"
              >
                {{ costPerCall }}
              </span>
            </template>
          </ReportingCardStats>

          <ReportingCardStats
            :icon="['fas', 'hand-holding-usd']"
            :stats-number="totalSaleCount"
            stats-title="Sales"
            :icon-color="ICON_COLORS.sales"
            data-cy="card-sales"
          >
            <!-- Same title and number styles as the card's own, on its right -->
            <template #aside>
              <h6
                title="CPA, cost per acquisition - Total Spent divided by Sales. Calls without a sale count toward the cost"
                class="card-title text-uppercase text-muted mb-1 card-ratio-title"
                data-cy="card-sales-cpa-title"
              >
                CPA
              </h6>
              <span
                class="h5 font-weight-bold mb-0"
                data-cy="card-sales-cpa"
              >
                {{ salesCostPerSale }}
              </span>
            </template>
          </ReportingCardStats>

          <ReportingCardStats
            :icon="['fas', 'clock']"
            :stats-text="secondsToDuration(callLengthPercentiles[callLengthPercentile])"
            stats-title="Call Length"
            :icon-color="ICON_COLORS.callLength"
            data-cy="card-call-length"
          >
            <template #title-extra>
              <span class="call-length-percentiles">
                <button
                  v-for="percentile in CALL_LENGTH_PERCENTILES"
                  :key="percentile.key"
                  :title="callLengthTooltip(percentile)"
                  type="button"
                  class="call-length-percentiles__option"
                  :class="{ 'call-length-percentiles__option--active': percentile.key === callLengthPercentile }"
                  :aria-pressed="percentile.key === callLengthPercentile ? 'true' : 'false'"
                  data-cy="call-length-percentile"
                  @click.stop="callLengthPercentile = percentile.key"
                >
                  {{ percentile.label }}
                </button>
              </span>
            </template>
          </ReportingCardStats>
        </div>
      </template>
    </Panel>

    <!-- Every call in the window, whatever the filters below: performance loads with the filter options, disputes
         with the disputed calls -->
    <div
      class="d-flex flex-wrap call-logs-summary"
      data-cy="call-logs-summary"
    >
      <Panel
        hide-header
        class="call-logs-summary__card call-logs-summary__card--performance"
      >
        <template #content>
          <PerformanceCard
            :channels="filterOptions.channelPerformance"
            :traffic-sources="filterOptions.trafficSourcePerformance"
            :campaigns="filterOptions.campaignPerformance"
            :channel-colors="channelColors"
            @select="selectFromPerformance"
          />
        </template>
      </Panel>

      <Panel
        hide-header
        class="call-logs-summary__card call-logs-summary__card--disputes"
      >
        <template #content>
          <DisputedCallsCard
            :disputed-calls="disputedCalls"
            @view-all="showDisputedCallsModal"
          />
        </template>
      </Panel>
    </div>

    <Panel
      hide-header
      class="call-logs-filters"
    >
      <template #content>
        <div
          class="d-flex flex-wrap align-items-center call-logs-filter-bar"
          data-cy="call-logs-filter-bar"
        >
          <SearchInput
            :value="searchText"
            class="call-logs-filter-bar__search"
            placeholder="Search call ID, caller ID, campaign, note"
            :max-length="searchMaxLength"
            data-cy="filter-search"
            @input="onSearchInput"
            @submit="applySearch"
            @clear="clearSearch"
          />

          <MultiSelectFilter
            label="Campaign"
            :options="filterOptions.campaignIds"
            :value="filter.campaignIds"
            data-cy="filter-campaigns"
            @input="setFilter('campaignIds', $event)"
          />

          <MultiSelectFilter
            label="Channel"
            :options="filterOptions.channels"
            :value="filter.channels"
            data-cy="filter-channels"
            @input="setFilter('channels', $event)"
          />

          <MultiSelectFilter
            label="Traffic Source"
            :options="filterOptions.trafficSourceIds"
            :value="filter.trafficSourceIds"
            data-cy="filter-traffic-sources"
            @input="setFilter('trafficSourceIds', $event)"
          />

          <!-- Same look as the multi-select filters next to it -->
          <b-dropdown
            variant="outline-secondary"
            size="sm"
            toggle-class="multi-select-filter__toggle"
            data-cy="filter-sale"
          >
            <template #button-content>
              {{ saleFilterLabel }}
            </template>

            <b-dropdown-item
              v-for="option in saleFilterOptions"
              :key="String(option.value)"
              :active="filter.isSaleReported === option.value"
              data-cy="filter-sale-option"
              @click="setFilter('isSaleReported', option.value)"
            >
              {{ option.text }}
            </b-dropdown-item>
          </b-dropdown>

          <!-- No auto-refresh here: the new calls check covers new calls, and a reload behind its back would show
               them without the button or the highlight -->
          <DashboardFilters
            class="call-logs-filter-bar__dates"
            :refresh="refreshCallLogs"
            :date-range="dateRange"
            :time-zone="timeZone"
            :max-date-range-in-days="90"
            compact
            @date-range-updated="handleDateRangeUpdate"
            @date-range-label="dateRangeLabel = $event"
            @update-time-zone="handleTimeZoneUpdate"
          />
        </div>
      </template>
    </Panel>

    <Panel
      size="small"
      class="call-logs-panel"
    >
      <template #header>
        <div class="d-flex align-items-center call-logs-panel__heading">
          <span data-cy="heading">Call Logs</span>

          <button
            v-if="newCallCount > 0"
            type="button"
            class="btn btn-info btn-sm ml-4 call-logs-new-calls"
            data-cy="new-calls"
            @click="showNewCalls"
          >
            <font-awesome-icon
              class="mr-1"
              :icon="['fas', 'arrow-up']"
            />
            {{ newCallCount }} new {{ newCallCount === 1 ? 'call' : 'calls' }}
          </button>

          <div
            class="ml-auto d-flex align-items-center call-logs-panel__actions"
            data-cy="call-logs-actions"
          >
            <BaseButtonLoader
              v-if="totalItems > 0"
              size="sm"
              nowrap
              :loading="isExportLoading"
              variant="secondary"
              @click="() => exportToFile()"
            >
              Export CSV
            </BaseButtonLoader>

            <BaseButtonLoader
              class="ml-2"
              size="sm"
              nowrap
              variant="secondary"
              @click="viewBlockedChannels"
            >
              Blocked channels
            </BaseButtonLoader>
          </div>
        </div>
      </template>

      <template #content>
        <b-row>
          <b-table
            ref="callLogsTable"
            :fields="fields"
            primary-key="callId"
            data-cy="table-buyer-call-logs"
            :items="loadBuyerCallLogs"
            :busy.sync="isLoading"
            thead-class="text-nowrap"
            :tbody-tr-class="callRowClass"
            :tbody-tr-attr="item => item && { 'data-call-id': item.callId }"
            responsive
            :per-page="pageSize"
            :current-page="currentPage"
            :sort-desc.sync="sortDesc"
            sort-by="timestamp"
            class="col-12"
          >
            <template #cell(timestamp)="row">
              <div class="text-nowrap">
                {{ dateFormat(row.item.timestamp, 'MMM DD hh:mm:ss A') }}
              </div>
            </template>

            <template #cell(trafficSourceName)="row">
              <div
                :title="trafficSourceTooltip(row.item)"
                class="text-nowrap"
                data-cy="call-traffic-source"
              >
                {{ row.item.trafficSourceName }} <small class="text-muted">{{ shortTrafficSourceType(row.item.trafficSourceType) }}</small>
              </div>
            </template>

            <template #cell(buyerCampaignName)="row">
              <!-- The full name on hover, only when the cell cuts it -->
              <div
                :title="campaignNameTooltip(row.item.buyerCampaignName)"
                class="text-nowrap"
                data-cy="call-campaign-name"
              >
                {{ shortCampaignName(row.item.buyerCampaignName) }}
              </div>
            </template>

            <template #cell(channel)="row">
              <!-- The block icon shows only while the pointer is over the channel (or keyboard focus is in it) -->
              <div class="d-inline-flex align-items-center call-channel">
                <ChannelPill
                  v-if="row.item.channel !== null && row.item.channel !== undefined"
                  :channel="row.item.channel"
                  :color="channelColors[row.item.channel]"
                  data-cy="call-channel"
                  @click="filterByChannel"
                />

                <button
                  v-if="row.item.channel && canManageCampaigns"
                  :title="`Block channel ${row.item.channel}`"
                  type="button"
                  class="btn btn-link btn-sm call-channel__block"
                  :aria-label="`Block channel ${row.item.channel}`"
                  data-cy="call-block-channel"
                  @click="showBlockChannelModal(row.item)"
                >
                  <font-awesome-icon :icon="['fas', 'ban']" />
                </button>
              </div>
            </template>

            <template #cell(callerId)="row">
              <div class="d-flex align-items-center text-nowrap">
                <!-- Focusable, so keyboard users reach the Call ID too: focus shows it, Enter or Space moves into it -->
                <span
                  :id="`caller-id-${row.index}`"
                  tabindex="0"
                  role="button"
                  :aria-label="`Caller ID ${row.item.callerId || 'unknown'}, show call ID`"
                  :aria-expanded="callIdPopoverRow === row.index ? 'true' : 'false'"
                  class="call-caller-id"
                  data-cy="caller-id"
                  @mouseenter="hoverCallerId(row.index)"
                  @mouseleave="leaveCallIdPopover"
                  @focus="openCallIdPopover(row.index)"
                  @blur="onCallerIdBlur"
                  @keydown.enter.prevent="enterCallIdPopover(row.index)"
                  @keydown.space.prevent="enterCallIdPopover(row.index)"
                  @keydown.esc="closeCallIdPopover"
                >
                  {{ row.item.callerId || '-' }}
                </span>

                <!-- The Call ID has no column of its own; it shows half a second into a hover and stays a second after,
                     so the pointer can reach its copy button, and stays while keyboard focus is in it -->
                <b-popover
                  :target="`caller-id-${row.index}`"
                  triggers="manual"
                  placement="top"
                  custom-class="call-id-popover"
                  :show="callIdPopoverRow === row.index"
                >
                  <div
                    class="d-flex align-items-center text-nowrap"
                    data-cy="call-id-popover"
                    @mouseenter="keepCallIdPopover"
                    @mouseleave="leaveCallIdPopover"
                    @focusout="onCallIdPopoverFocusOut($event, row.index)"
                    @keydown.esc="closeCallIdPopoverToCallerId(row.index)"
                  >
                    <span class="text-muted mr-1">Call ID</span>
                    {{ row.item.callId }}

                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 ml-2 call-icon-button"
                      title="Copy call ID"
                      aria-label="Copy call ID"
                      data-cy="copy-call-id"
                      @click="copyCallIdFromPopover(row.item.callId)"
                    >
                      <font-awesome-icon :icon="['fas', 'copy']" />
                    </button>
                  </div>
                </b-popover>

                <button
                  v-if="isCallerIdVisible(row.item.callerId)"
                  type="button"
                  class="btn btn-link btn-sm p-0 ml-2 call-icon-button"
                  title="Copy caller ID"
                  aria-label="Copy caller ID"
                  data-cy="copy-caller-id"
                  @click="copyCallerIdToClipboard(row.item.callerId)"
                >
                  <font-awesome-icon :icon="['fas', 'copy']" />
                </button>
              </div>
            </template>

            <template #cell(durationInSeconds)="row">
              <div class="text-nowrap text-right">
                {{ secondsToDuration(row.item.durationInSeconds) }}
              </div>
            </template>

            <template #cell(cost)="row">
              <div
                :title="isDisputed(row.item) ? disputedCostTooltip(row.item) : null"
                class="text-nowrap text-right"
                :class="{ 'call-cost--disputed': isDisputed(row.item) }"
                data-cy="call-cost"
              >
                {{ formatCurrency(row.item.cost) }}
              </div>
            </template>

            <template #cell(billable)="row">
              <CheckCircleMark
                :checked="isBillable(row.item)"
                :title="isBillable(row.item) ? 'Billable: the call converted' : 'Not billable'"
                data-cy="call-billable"
              />
            </template>

            <!-- Rows from an API without sale reporting have no isSaleReported: offer nothing that would only 404 -->
            <template #cell(sale)="row">
              <CheckToggle
                v-if="'isSaleReported' in row.item"
                :checked="row.item.isSaleReported"
                :check="() => reportSale(row.item)"
                :uncheck="() => undoSale(row.item)"
                check-title="Report sale"
                :checked-title="`${saleTooltip(row.item)}. Click to undo.`"
                checked-aria-label="Undo sale"
                uncheck-modal-title="Undo Sale"
                uncheck-modal-text="Undo sale for this call?"
                uncheck-confirm-text="Undo sale"
                check-data-cy="call-report-sale"
                uncheck-data-cy="call-undo-sale"
              />
            </template>

            <template #cell(agentNote)="row">
              <AgentNoteInput
                v-if="'isSaleReported' in row.item"
                class="call-agent-note"
                :account-id="accountId"
                :call-id="row.item.callId"
                :value="row.item.agentNote"
                data-cy="call-agent-note"
                @saved="$set(row.item, 'agentNote', $event)"
              />
            </template>

            <template #cell(recordingUrl)="row">
              <button
                v-if="row.item.recordingUrl !== null && row.item.recordingUrl !== undefined"
                type="button"
                class="btn btn-link btn-sm p-0 call-icon-button"
                :style="{ color: ICON_COLORS.play }"
                title="Play recording"
                aria-label="Play recording"
                data-cy="play-recording"
                @click="openRecording(row.item.recordingUrl)"
              >
                <font-awesome-icon :icon="['fas', 'play']" />
              </button>
            </template>
          </b-table>
        </b-row>
        
        <div class="d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            size="sm"
            :total-rows="totalItems"
            :per-page="pageSize"
          />
        </div>
      </template>
    </Panel>

    <b-modal
      id="confirm-block-channel-modal"
      ref="confirm-block-channel-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Block Channel"
      ok-title="Block Channel"
      button-size="sm"
      @ok="confirmBlockChannel"
    >
      <p
        v-if="selectedCall"
        class="mb-2"
      >
        Are you sure you want to block <strong>{{ selectedCall.channel }}</strong> channel?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-block-channel-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="danger"
          @click="confirmBlockChannel"
        >
          Block
        </BaseButtonLoader>
      </template>
    </b-modal>

    <b-modal
      id="disputed-calls-modal"
      ref="disputed-calls-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Disputed Calls"
      size="xl"
      ok-only
      ok-title="Close"
      button-size="sm"
    >
      <b-table
        :fields="disputedCallsFields"
        :items="disputedCalls"
        :busy="isLoadingDisputedCalls"
        thead-class="text-nowrap"
        tbody-tr-class="text-nowrap"
        responsive
        sort-desc
        sort-by="disputeResolvedTimestamp"
        :show-empty="true"
        empty-text="No disputed calls found"
      >
        <template #cell(disputeResolvedTimestamp)="row">
          <div class="text-nowrap">
            {{ row.item.disputeResolvedTimestamp ? dateFormat(row.item.disputeResolvedTimestamp, 'MMM DD hh:mm:ss A') : '-' }}
          </div>
        </template>

        <template #cell(disputedAmount)="row">
          <div
            class="text-nowrap text-right"
            :class="row.item.disputedAmount < 0 ? 'adjustment--lowered' : 'adjustment--raised'"
          >
            {{ formatCurrency(row.item.disputedAmount) }}
          </div>
        </template>

        <template #cell(callerId)="row">
          <div class="text-nowrap">
            {{ row.item.callerId || '-' }}
          </div>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>

import DashboardFilters from '@/components/DashboardFilters.vue'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import callLogsMixin from '@/mixins/call-logs.mixin'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { showSuccessMessage, showErrorMessage, getApiErrorMessage } from '@/notification-utils'
import BuyerCallLogsAPI from '@/api/buyer-call-logs.api'
import CallDispositionAPI from '@/api/call-disposition.api'
import formatCurrency from '@/utils/formatCurrency'
import ReportingCardStats from '@/components/common/ReportingCardStats.vue'
import { formatUtcInTimeZone } from '@/utils/dateTimeUtils'
import CheckToggle from '@/components/common/CheckToggle.vue'
import CheckCircleMark from '@/components/common/CheckCircleMark.vue'
import AgentNoteInput from '@/components/common/AgentNoteInput.vue'
import MultiSelectFilter from '@/components/common/MultiSelectFilter.vue'
import SearchInput from '@/components/common/SearchInput.vue'
import PerformanceCard from '@/components/buyer/PerformanceCard.vue'
import ChannelPill from '@/components/buyer/ChannelPill.vue'
import { CHANNEL_COLORS, rankChannelColors } from '@/utils/channelColors'
import DisputedCallsCard from '@/components/buyer/DisputedCallsCard.vue'
import { BDropdown, BDropdownItem, BPopover } from 'bootstrap-vue'

const CALL_ID_POPOVER_SHOW_DELAY_MS = 500

const CAMPAIGN_NAME_MAX_LENGTH = 30

const NEW_CALLS_POLL_INTERVAL_MS = 20 * 1000

// The page's icons in the channel badges' muted palette, so nothing on the board shouts louder than the data
const ICON_COLORS = Object.freeze({
  totalCalls: CHANNEL_COLORS[5], // cyan
  totalSpent: CHANNEL_COLORS[0], // blue
  // A muted green, not the palette's bluish teal: sales read as green
  sales: '#5fb96a',
  callLength: CHANNEL_COLORS[1], // purple
  play: CHANNEL_COLORS[5] // cyan
})

// The average, then percentiles by nearest rank over the calls with a duration, all from the totals; the average is
// shown first
const CALL_LENGTH_PERCENTILES = Object.freeze([
  {
    key: 'avg',
    label: 'AVG',
    explain: length => `Average - total call time divided by the number of calls, ${length} per call`
  },
  {
    key: 'p25',
    label: 'P25',
    explain: length => `25th percentile - a quarter of calls lasted ${length} or less, three quarters lasted longer`
  },
  {
    key: 'p50',
    label: 'P50',
    explain: length => `Median - half of the calls lasted ${length} or less, half lasted longer`
  },
  {
    key: 'p75',
    label: 'P75',
    explain: length => `75th percentile - three quarters of calls lasted ${length} or less, a quarter lasted longer`
  },
  {
    key: 'p95',
    label: 'P95',
    explain: length => `95th percentile - 95 in 100 calls lasted ${length} or less, only the longest 5 went past it`
  }
])

// Matches the row fade-out animation
const NEW_CALLS_HIGHLIGHT_MS = 4000
const CALL_ID_POPOVER_HIDE_DELAY_MS = 1000

const TABLE_RELOAD_TIMEOUT_MS = 10000

export default {
  name: 'BuyerCallLogs',

  title: 'Call Logs',

  components: {
    DashboardFilters,
    ReportingCardStats,
    CheckToggle,
    CheckCircleMark,
    AgentNoteInput,
    MultiSelectFilter,
    SearchInput,
    PerformanceCard,
    ChannelPill,
    DisputedCallsCard,
    BDropdown,
    BDropdownItem,
    BPopover
  },

  mixins: [dateFormatMixin, callLogsMixin, copyToClipboardMixin],

  data() {
    return {
      // From the date bar: "Last 7 Days", or "Oct 19 - Oct 26" for a custom range
      dateRangeLabel: '',
      fields: Object.freeze([
        // No title: the play icon speaks for itself
        { key: 'recordingUrl', label: '', class: 'call-recording-cell', thAttr: { 'aria-label': 'Recording' } },
        { key: 'timestamp', label: 'Date', sortable: true, sortDirection: 'desc' },
        { key: 'buyerCampaignName', label: 'Campaign' },
        { key: 'channel', label: 'Channel' },
        { key: 'callerId', label: 'Caller ID' },
        { key: 'trafficSourceName', label: 'Traffic Source' },
        { key: 'durationInSeconds', label: 'Duration', class: 'text-right', thClass: 'text-right' },
        { key: 'cost', label: 'Price', class: 'text-right', thClass: 'text-right' },
        { key: 'billable', label: 'Billable', class: 'text-center', thClass: 'text-center' },
        { key: 'sale', label: 'Sale' },
        { key: 'agentNote', label: 'Agent Note', class: 'call-agent-note-cell' }
      ]),
      disputedCallsFields: Object.freeze([
        { key: 'disputeResolvedTimestamp', label: 'Dispute Resolved', sortable: true, sortDirection: 'desc' },
        { key: 'buyerCampaignName', label: 'Campaign', sortable: true },
        { key: 'callerId', label: 'Caller ID', sortable: false },
        { key: 'disputedAmount', label: 'Amount', sortable: true, class: 'text-right', thClass: 'text-right' }
      ]),
      saleFilterOptions: Object.freeze([
        { value: null, text: 'All' },
        { value: true, text: 'Yes' },
        { value: false, text: 'No' }
      ]),
      totalCost: 0,
      totalSaleCount: 0,
      CALL_LENGTH_PERCENTILES,
      ICON_COLORS,
      // Call length in seconds, the average and by percentile, from the totals; the card shows the chosen one
      callLengthPercentiles: { avg: 0, p25: 0, p50: 0, p75: 0, p95: 0 },
      callLengthPercentile: 'avg',
      sortDesc: true,
      // Finalized calls the totals count beyond the rows last loaded; the "N new calls" button shows them
      newCallCount: 0,
      // Rows that came in through that button, highlighted for a moment
      highlightedCallIds: [],
      disputedCalls: [],
      // Row whose Call ID popover is up
      callIdPopoverRow: null,
      isLoadingDisputedCalls: false,
      selectedCall: null
    }
  },

  computed: {
    // The time frame on the first card says which one every summary card and the Performance and Disputes cards use
    totalCallsTitle() {
      return this.dateRangeLabel ? `Total Calls (${this.dateRangeLabel})` : 'Total Calls'
    },

    // RPC: Total Spent per call under the same filters, as the Performance card's RPC column
    costPerCall() {
      return this.totalItems > 0 ? this.formatCurrency(this.totalCost / this.totalItems) : '-'
    },

    // Total Spent per sale under the same filters, as in the Performance card; no sale, no CPA
    salesCostPerSale() {
      return this.totalSaleCount > 0 ? this.formatCurrency(this.totalCost / this.totalSaleCount) : '-'
    },

    // By the window's calls per channel, whatever the filters: a channel keeps its colour while the buyer filters
    channelColors() {
      return rankChannelColors(this.filterOptions.channelPerformance)
    },

    canManageCampaigns() {
      return this.$store.getters['auth/hasPermission']('campaigns')
    },

    // "Sale" while unfiltered, like the other filter buttons; "Sale: Yes" / "Sale: No" once set
    saleFilterLabel() {
      const selected = this.saleFilterOptions.find(option => option.value === this.filter.isSaleReported)

      return selected && selected.value !== null ? `Sale: ${selected.text}` : 'Sale'
    }
  },

  watch: {
    dateRange() {
      this.loadDisputedCallsCount()
    },
    timeZone() {
      this.loadDisputedCallsCount()
    }
  },

  created() {
    // Not reactive: bookkeeping for the new calls check
    this.newCallsPollTimer = null
    this.newCallsHighlightTimer = null
    // The total call count the loaded rows were read with, and the call ids seen under the current window and filter
    this.loadedCallCount = 0
    this.knownCallIds = new Set()
    this.knownCallIdsKey = null
    this.highlightNextLoad = false
  },

  mounted() {
    this.loadDisputedCallsCount()

    this.newCallsPollTimer = setInterval(() => this.checkForNewCalls(), NEW_CALLS_POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },

  beforeDestroy() {
    clearInterval(this.newCallsPollTimer)
    clearTimeout(this.newCallsHighlightTimer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.closeCallIdPopover()
  },

  methods: {
    formatCurrency,

    async loadBuyerCallLogs(ctx) { // eslint-disable-line
      try {
        this.isLoading = true

        if (this.takeTotalsReload()) {
          await this.getTotalCalls()

          this.loadedCallCount = this.totalItems
          this.newCallCount = 0
        }

        const response = await BuyerCallLogsAPI.getBuyerCallLogs(this.accountId, {
          from: this.dateRange[0],
          to: this.dateRange[1],
          timeZone: this.timeZone,
          pageSize: ctx.perPage,
          pageNumber: ctx.currentPage,
          // Date is the only sortable column, sorted by the API across all pages
          sortDescending: ctx.sortDesc,
          filter: this.filter
        })

        this.calls = response.data

        this.rememberLoadedCalls(response.data)

        return response.data
      } catch (error) {
        if (error.response?.status === 500) {
          showErrorMessage(`Unable to load call logs`)
        } else {
          showErrorMessage(error.response?.data?.title || `Unable to load call logs`)
        }

        this.calls = []

        return []
      } finally {
        this.isLoading = false
      }
    },

    async getTotalCalls() {
      this.applyTotals(await this.fetchTotals())
    },

    async fetchTotals() {
      const response = await BuyerCallLogsAPI.getBuyerTotalCalls(this.accountId, {
        from: this.dateRange[0],
        to: this.dateRange[1],
        timeZone: this.timeZone,
        filter: this.filter
      })

      return response.data
    },

    applyTotals(totals) {
      this.totalItems = totals.totalCallCount
      this.totalCost = totals.totalCost
      this.totalSaleCount = totals.totalSaleCount || 0
      this.callLengthPercentiles = {
        avg: totals.averageDurationInSeconds || 0,
        p25: totals.durationP25 || 0,
        p50: totals.durationP50 || 0,
        p75: totals.durationP75 || 0,
        p95: totals.durationP95 || 0
      }
    },

    async fetchCallCount() {
      const response = await BuyerCallLogsAPI.getBuyerCallCount(this.accountId, {
        from: this.dateRange[0],
        to: this.dateRange[1],
        timeZone: this.timeZone,
        filter: this.filter
      })

      return response.data.totalCallCount
    },

    // Hover help for a percentile, with the value it stands for in this window
    callLengthTooltip(percentile) {
      const length = this.secondsToDuration(this.callLengthPercentiles[percentile.key])

      return percentile.explain(length)
    },

    // What the loaded rows were read with; a change starts a new set of known calls
    callLogsQueryKey() {
      return JSON.stringify([this.dateRange, this.timeZone, this.filter])
    },

    /**
     * Polled while the tab is visible. Calls appear here once finalized, which can be after newer calls are already
     * listed, so it compares the call count rather than dates. It asks the cheap count first and the full totals
     * only when the count moved. The stat cards and the page count then follow at once; the rows wait for the
     * "N new calls" button, so the table never moves under the buyer.
     */
    async checkForNewCalls() {
      if (document.hidden || this.isLoading) {
        return
      }

      const key = this.callLogsQueryKey()

      try {
        const count = await this.fetchCallCount()

        if (key !== this.callLogsQueryKey() || this.isLoading || count === this.totalItems) {
          return
        }

        const totals = await this.fetchTotals()

        // The window or filter changed, or a load started, while it ran: its totals may be the old ones
        if (key !== this.callLogsQueryKey() || this.isLoading) {
          return
        }

        this.applyTotals(totals)
        this.newCallCount = Math.max(0, totals.totalCallCount - this.loadedCallCount)
      } catch (error) {
        // The next check tries again; the page works without it
        console.error('Error checking for new calls:', error)
      }
    },

    onVisibilityChange() {
      if (!document.hidden) {
        this.checkForNewCalls()
      }
    },

    /**
     * The refresh button: everything on the page again, on the same page and sort. It also picks up changes to calls
     * already listed that the new calls check cannot see (a sale reported elsewhere, a resolved dispute, a late
     * recording), and settles the new calls count, so the button goes away; rows not seen before light up.
     */
    refreshCallLogs() {
      this.newCallCount = 0
      this.highlightNextLoad = true
      this.totalsReloadPending = true

      this.loadFilterOptions()
      this.loadDisputedCallsCount()

      return this.reloadData()
    },

    // Newest first from page 1, with the summary cards; the rows not seen before light up for a moment
    showNewCalls() {
      this.newCallCount = 0
      this.highlightNextLoad = true

      this.loadFilterOptions()
      this.loadDisputedCallsCount()

      this.resetTable()

      if (!this.sortDesc) {
        // The sort change reloads the table by itself
        this.sortDesc = true

        return
      }

      return this.reloadData()
    },

    rememberLoadedCalls(calls) {
      const key = this.callLogsQueryKey()

      if (key !== this.knownCallIdsKey) {
        this.knownCallIds = new Set()
        this.knownCallIdsKey = key
      }

      if (this.highlightNextLoad) {
        this.highlightNextLoad = false
        this.highlightedCallIds = calls.map(call => call.callId).filter(callId => !this.knownCallIds.has(callId))

        clearTimeout(this.newCallsHighlightTimer)
        this.newCallsHighlightTimer = setTimeout(() => { this.highlightedCallIds = [] }, NEW_CALLS_HIGHLIGHT_MS)
      }

      calls.forEach(call => this.knownCallIds.add(call.callId))
    },

    callRowClass(item) {
      return item && this.highlightedCallIds.includes(item.callId) ? ['text-nowrap', 'call-row--new'] : 'text-nowrap'
    },

    // Called by the call logs mixin; channels are plain values, sent as they are
    async fetchFilterOptions() {
      const { data } = await BuyerCallLogsAPI.getCallFilterOptions(this.accountId, {
        from: this.dateRange[0],
        to: this.dateRange[1],
        timeZone: this.timeZone
      })

      return {
        campaignIds: data?.campaigns || [],
        channels: (data?.channels || []).map(channel => ({ id: channel, name: channel })),
        trafficSourceIds: data?.trafficSources || [],
        // Not filters: the Performance card's rows
        channelPerformance: data?.channelPerformance || [],
        trafficSourcePerformance: data?.trafficSourcePerformance || [],
        campaignPerformance: data?.campaignPerformance || []
      }
    },

    openRecording(url) {
      window.open(url)
    },

    async exportToFile() {
      try {
        this.isExportLoading = true

        const exportResponse = await BuyerCallLogsAPI.exportBuyerCalls(this.accountId, {
          from: this.dateRange[0],
          to: this.dateRange[1],
          timeZone: this.timeZone,
          filter: this.filter
        })

        window.location.href = exportResponse.data.url
      }
      catch (error) {
        if (error.response.status === 500) {
          showErrorMessage(`Unable to export calls to file`)
        }
        else {
          showErrorMessage(error.response.data.title)
        }        
      }
      finally {
        this.isExportLoading = false      
      }
    },

    showBlockChannelModal(call) {
      if (!this.canManageCampaigns) return
      this.selectedCall = call

      this.$bvModal.show('confirm-block-channel-modal')
    },   
    
    // One channel only: replaces whatever the Channel dropdown had, then reloads from page 1 like any filter change
    // Converted (the original conversion amount, net cost minus the dispute adjustment, is above zero, so a call refunded
    // later stays billable) or billed by an admin adjustment (net cost above zero). Same rule as CallerIdMasker in
    // ReportingService, which shows exactly these calls' caller ids in full.
    isBillable(call) {
      const cost = call.cost || 0

      return cost - (call.disputedAmount || 0) > 0 || cost > 0
    },

    // A resolved dispute: it adjusted the cost or has a resolution date. A dispute still open has neither in the row.
    isDisputed(call) {
      return Boolean(call.disputedAmount) || Boolean(call.disputeResolvedTimestamp)
    },

    // The cost before the dispute and the adjustment, which the shown (net) cost is the sum of, plus why an admin billed
    // a short call on a second line
    disputedCostTooltip(call) {
      const amount = call.disputedAmount || 0
      const initialCost = (call.cost || 0) - amount
      const hangUp = this.buyerHangUpExplanation(call)

      return `Disputed: initial cost ${formatCurrency(initialCost)}, ${formatCurrency(amount)} adjustment` +
        (hangUp ? `\n${hangUp}` : '')
    },

    // Why an admin billed a short call: the buyer's side ended it before the conversion duration. Only for an
    // adjustment that raised the cost; a Caller or System hang-up gets no text on purpose, it doesn't justify the charge.
    // The end call source and durations are saved when the admin adjusts the call, so older adjustments have none.
    // A postback bid keeps a minimum duration, but the buyer's postback decides its conversion, so no text either; nor
    // without a saved bid conversion type, when we can't tell. Billing checks postback the same way, by name only.
    buyerHangUpExplanation(call) {
      const connected = Number(call.disputeConnectedDurationInSeconds)
      const required = Number(call.disputeConversionDurationRequiredInSeconds)
      const bidConversionType = (call.disputeBidConversionType || '').toLowerCase()

      if (!(call.disputedAmount > 0)) return null
      if ((call.disputeEndCallSource || '').toLowerCase() !== 'target') return null
      if (!bidConversionType || bidConversionType === 'postback') return null
      if (call.disputeConnectedDurationInSeconds == null || call.disputeConversionDurationRequiredInSeconds == null) return null
      if (!Number.isFinite(connected) || !Number.isFinite(required) || connected >= required) return null

      return `Buyer side hung up after ${connected}s connected, conversion required ${required}s.`
    },

    // A name clicked in the Performance card: that channel, traffic source or campaign alone, like a row's channel
    selectFromPerformance({ field, id }) {
      return this.setFilter(field, [id])
    },

    filterByChannel(channel) {
      return this.setFilter('channels', [channel])
    },

    async confirmBlockChannel() {
      if (!this.canManageCampaigns) return
      try {
        this.isLoading = true

        await BuyerCallLogsAPI.blockChannel(this.accountId, {
          callId: this.selectedCall.callId,
          channel: this.selectedCall.channel
        })

        showSuccessMessage('Successfully blocked channel ' + this.selectedCall.channel)

        this.selectedCall = null
        this.$bvModal.hide('confirm-block-channel-modal')
      }
      catch (error) {
        if (error.response.status === 500) {
          showErrorMessage(`Unable to block a channel`)
        }
        else {
          showErrorMessage(error.response.data.title)
        }        
      }
      finally {
        this.isLoading = false
        
        this.$bvModal.hide('confirm-block-channel-modal')
      }
    },

    saleTooltip(call) {
      // Only a webhook names its source; a sale the buyer marked by hand needs no "via"
      const reported = call.saleReportedSource === 'Webhook' ? 'Reported via Webhook' : 'Reported'

      if (!call.saleReportedChangedAt) {
        return reported
      }

      // UTC from the API, unlike `timestamp`, which arrives already in the page's time zone.
      return `${reported} on ${formatUtcInTimeZone(call.saleReportedChangedAt, this.timeZone, 'MMM DD hh:mm:ss A')}`
    },

    // refresh() only starts the reload; this resolves once the table has the new rows
    // (or after a timeout, so a provider that never settles can't lock the action forever).
    reloadTableAndWait() {
      const table = this.$refs.callLogsTable

      if (!table) {
        return Promise.resolve()
      }

      // bootstrap-vue's refresh(): while its own load runs, the new one is queued behind it, so the first
      // `refreshed` brings that earlier load's (stale) rows and we wait for the second; while busy only from
      // outside (`isLoading` set by the page), the request is dropped and no `refreshed` comes at all.
      const isLoadRunning = table.localBusy

      if (table.computedBusy && !isLoadRunning) {
        return Promise.resolve()
      }

      return new Promise(resolve => {
        let remaining = isLoadRunning ? 2 : 1

        const finish = () => {
          clearTimeout(timeout)
          table.$off('refreshed', onRefreshed)
          resolve()
        }

        const onRefreshed = () => {
          remaining -= 1

          if (remaining === 0) {
            finish()
          }
        }

        const timeout = setTimeout(finish, TABLE_RELOAD_TIMEOUT_MS)

        table.$on('refreshed', onRefreshed)

        table.refresh()
      })
    },

    /**
     * Shows the change on the row straight away. A refresh asked for while the table is already loading
     * (auto-refresh, page change) settles on that earlier load's rows first, so waiting for the reload
     * alone could leave the old action on the row.
     */
    applySaleStateToRow(call, isSale) {
      this.$set(call, 'isSaleReported', isSale)
      this.$set(call, 'saleReportedSource', 'CallLogs')
      this.$set(call, 'saleReportedChangedAt', new Date().toISOString())
    },

    /**
     * A sale changes the Sales card, its CPA and the Performance card's sales and CPA, but not the call count, so the
     * new calls check would never refresh them. Fetched again after each report or undo; a failure leaves the previous
     * figures, as the stored sale already succeeded.
     */
    async refreshSaleFigures() {
      const key = this.callLogsQueryKey()

      this.loadFilterOptions()

      try {
        const totals = await this.fetchTotals()

        if (key !== this.callLogsQueryKey()) {
          return
        }

        this.applyTotals(totals)
        this.newCallCount = Math.max(0, totals.totalCallCount - this.loadedCallCount)
      } catch (error) {
        console.error('Error refreshing totals after a sale change:', error)
      }
    },

    // No confirmation: a report is cheap and Undo takes it back. CheckToggle guards the double click.
    async reportSale(call) {
      try {
        await CallDispositionAPI.setSale(this.accountId, { callId: call.callId, sale: true, source: 'CallLogs' })

        this.applySaleStateToRow(call, true)
        this.refreshSaleFigures()

        // The reload brings the stored source and time, which differ when the call already was a sale
        await this.reloadTableAndWait()

        showSuccessMessage('Sale reported')
      }
      catch (error) {
        showErrorMessage(getApiErrorMessage(error, 'Unable to report a sale'))
      }
    },

    // Called from CheckToggle's confirmation modal. Rejects on failure so the modal stays open; resolves
    // once the change is stored, so the modal closes without waiting out the table reload.
    async undoSale(call) {
      try {
        await CallDispositionAPI.setSale(this.accountId, { callId: call.callId, sale: false, source: 'CallLogs' })
      }
      catch (error) {
        showErrorMessage(getApiErrorMessage(error, 'Unable to undo a sale'))

        throw error
      }

      this.applySaleStateToRow(call, false)
      this.refreshSaleFigures()

      this.reloadTableAndWait().then(() => showSuccessMessage('Sale undone'))
    },

    copyCallIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Call ID copied to clipboard')
    },

    // Long campaign names stretch the table: cut to 30 characters, the hover text has the full name
    shortCampaignName(name) {
      return name && name.length > CAMPAIGN_NAME_MAX_LENGTH ? `${name.slice(0, CAMPAIGN_NAME_MAX_LENGTH).trimEnd()}...` : name
    },

    campaignNameTooltip(name) {
      return name && name.length > CAMPAIGN_NAME_MAX_LENGTH ? name : null
    },

    // "Inbound" / "Outbound" -> "In" / "Out"; any other type as it comes
    shortTrafficSourceType(type) {
      return { inbound: 'In', outbound: 'Out' }[String(type).toLowerCase()] || type
    },

    // "Google Outbound" for the cell's "Google Out"
    trafficSourceTooltip(call) {
      const type = { inbound: 'Inbound', outbound: 'Outbound' }[String(call.trafficSourceType).toLowerCase()] || ''

      return [call.trafficSourceName, type].filter(Boolean).join(' ')
    },

    // Opened and closed here rather than by the popover's own hover trigger, which leaves it up after a click
    hoverCallerId(rowIndex) {
      this.clearCallIdPopoverTimers()
      this.callIdPopoverShowTimer = setTimeout(() => this.openCallIdPopover(rowIndex), CALL_ID_POPOVER_SHOW_DELAY_MS)
    },

    leaveCallIdPopover() {
      this.clearCallIdPopoverTimers()
      this.callIdPopoverHideTimer = setTimeout(() => this.closeCallIdPopoverUnlessHovered(), CALL_ID_POPOVER_HIDE_DELAY_MS)
    },

    // The popover can open over the caller id, and its padding is outside the content's mouse events. Keyboard focus on
    // either keeps it too.
    closeCallIdPopoverUnlessHovered() {
      if (document.querySelector('.popover:hover, [data-cy="caller-id"]:hover') || this.isFocusOnCallIdPopover()) {
        this.leaveCallIdPopover()
        return
      }

      this.closeCallIdPopover()
    },

    isFocusOnCallIdPopover(element = document.activeElement) {
      return Boolean(element?.closest?.('.call-id-popover, [data-cy="caller-id"]'))
    },

    // Enter or Space on the caller id: open now and move focus to the copy button inside
    enterCallIdPopover(rowIndex) {
      this.openCallIdPopover(rowIndex)

      this.$nextTick(() => {
        // The popover renders after the next tick; give it one more frame to be in the body
        setTimeout(() => document.querySelector('.call-id-popover [data-cy="copy-call-id"]')?.focus(), 0)
      })
    },

    // Leaving the caller id by keyboard closes it, unless focus went into the popover
    onCallerIdBlur(event) {
      if (this.isFocusOnCallIdPopover(event.relatedTarget)) {
        return
      }

      this.leaveCallIdPopover()
    },

    onCallIdPopoverFocusOut(event, rowIndex) {
      if (this.isFocusOnCallIdPopover(event.relatedTarget)) {
        return
      }

      // Focus left both: back to how hover leaves it
      if (this.callIdPopoverRow === rowIndex) {
        this.leaveCallIdPopover()
      }
    },

    // Escape inside the popover: close it and put focus back where it came from
    closeCallIdPopoverToCallerId(rowIndex) {
      this.closeCallIdPopover()
      document.getElementById(`caller-id-${rowIndex}`)?.focus({ preventScroll: true })
    },

    keepCallIdPopover() {
      this.clearCallIdPopoverTimers()
    },

    openCallIdPopover(rowIndex) {
      this.clearCallIdPopoverTimers()
      this.callIdPopoverRow = rowIndex
      document.addEventListener('mousedown', this.closeCallIdPopoverOnOutsideClick)
    },

    closeCallIdPopover() {
      this.clearCallIdPopoverTimers()
      this.callIdPopoverRow = null
      document.removeEventListener('mousedown', this.closeCallIdPopoverOnOutsideClick)
    },

    closeCallIdPopoverOnOutsideClick(event) {
      if (event.target.closest?.('.popover')) {
        return
      }

      this.closeCallIdPopover()
    },

    copyCallIdFromPopover(callId) {
      this.copyCallIdToClipboard(callId)
      this.closeCallIdPopover()
    },

    clearCallIdPopoverTimers() {
      clearTimeout(this.callIdPopoverShowTimer)
      clearTimeout(this.callIdPopoverHideTimer)
    },

    // The API masks the caller id of calls the buyer didn't pay for, keeping only the last digits
    isCallerIdVisible(callerId) {
      return Boolean(callerId) && !callerId.includes('*')
    },

    copyCallerIdToClipboard(callerId) {
      this.copyTextToClipboard(callerId)
      showSuccessMessage('Caller ID copied to clipboard')
    },
    
    async viewBlockedChannels() {
      window.open(this.$router.resolve({ name: 'BlockedChannels' }).href, '_blank')
    },

    async loadDisputedCallsCount() {
      try {
        const response = await BuyerCallLogsAPI.getBuyerDisputedCalls(this.accountId, {
          from: this.dateRange[0],
          to: this.dateRange[1],
          timeZone: this.timeZone,
          pageSize: 100,
          pageNumber: 1
        })

        this.disputedCalls = response.data || []
      } catch (error) {
        // The Disputes card would otherwise keep the previous window's calls
        console.error('Error loading disputed calls:', error)
        this.disputedCalls = []
      }
    },

    async loadDisputedCalls() {
      try {
        this.isLoadingDisputedCalls = true

        const response = await BuyerCallLogsAPI.getBuyerDisputedCalls(this.accountId, {
          from: this.dateRange[0],
          to: this.dateRange[1],
          timeZone: this.timeZone,
          pageSize: 100,
          pageNumber: 1
        })

        this.disputedCalls = response.data || []
      } catch (error) {
        console.error('Error loading disputed calls:', error)
        showErrorMessage('Failed to load disputed calls')
        this.disputedCalls = []
      } finally {
        this.isLoadingDisputedCalls = false
      }
    },

    async showDisputedCallsModal() {
      if (this.disputedCalls.length === 0) {
        await this.loadDisputedCalls()
      } else {
        this.isLoadingDisputedCalls = false
      }
      this.$bvModal.show('disputed-calls-modal')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/call-logs-page';
@import '@/assets/scss/components/call-logs-colors';

// Buyer only: the summary row between the stats and the filters. Traffic sources, channels and disputes side by side;
// they stack on a narrow screen
// The row has the panels' side margins, so its cards line up with the panels above and below whether they sit side by
// side or stack. A first and last card's own margins did that side by side only: stacked, the performance card ran to
// the screen's right edge and the disputes card from its left (docs/softphone-mobile-plan.md, P3.11).
.call-logs-summary {
  margin: 0.5rem $panel-margin 0;
  gap: 0.5rem;
}

.panel.call-logs-summary__card {
  margin: 0;
  min-width: 18rem;
}

.call-logs-summary__card--performance {
  flex: 3 1 0;
}

.call-logs-summary__card--disputes {
  flex: 1 1 0;
}

.call-logs-summary__card ::v-deep .panel__content {
  // stylelint-disable-next-line declaration-no-important -- over the p-3 utility Panel puts on its content
  padding: 0.75rem !important;
}

// Percentile options on the Call Length title line: small, the chosen one white and underlined like the Performance
// card's
// Its own small font and line height: at the card's 14px the row would grow taller than the other cards' titles and
// push the value down
.call-length-percentiles {
  margin-left: 0.375rem;
  white-space: nowrap;
  font-size: 0.7rem;
  line-height: 1.2;
}

.call-length-percentiles__option {
  padding: 0 0.125rem;
  border: 0;
  border-bottom: 1px solid transparent;
  background: none;
  color: $secondary;
  font-size: 0.7rem;
  line-height: 1.2;

  &:hover,
  &:focus {
    color: var(--white);
    outline: none;
  }

  &--active {
    border-bottom-color: #5b8def;
    color: var(--white);
  }
}

// Calls brought in by the "N new calls" button: a brief tint that fades out
::v-deep .call-row--new td {
  animation: call-row-new-fade 4s ease-out;
}

@keyframes call-row-new-fade {
  0%,
  40% {
    background-color: rgba(17, 202, 255, 0.18);
  }

  100% {
    background-color: transparent;
  }
}

.call-logs-new-calls {
  padding: 0.1rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.75rem;
}

// Ratio titles explain themselves on hover
.card-ratio-title {
  cursor: help;
}

// Icon-only native buttons (play, copy): keyboard reachable, but looking like the bare icons they replace
.call-icon-button {
  border: 0;
  line-height: 1;
  color: inherit;
  vertical-align: baseline;

  &:hover {
    filter: brightness(1.2);
  }
}

// The caller id opens the Call ID popover on focus; a visible ring shows keyboard users where they are
.call-caller-id:focus {
  outline: 1px dotted currentColor;
  outline-offset: 2px;
}

.adjustment--lowered {
  color: $call-logs-green;
}

.adjustment--raised {
  color: $call-logs-red;
}

// Same red as the Disputes card's warning triangle
.call-cost--disputed {
  color: $call-logs-red;
  font-weight: 600;
}

// Red, so it stands out, and hidden until the pointer is over the channel. Hidden keeps its space in the column, so
// it never covers the next cell's text; the negative margin lets it sit in this cell's right padding, so the column
// only grows by about the icon
.call-channel__block {
  padding: 0 0 0 0.25rem;
  margin-right: -0.5rem;
  color: $call-logs-red;
  visibility: hidden;

  // Stays red over Bootstrap's link hover colour
  &:hover,
  &:focus {
    color: $call-logs-red;
  }
}

.call-channel:hover .call-channel__block,
.call-channel:focus-within .call-channel__block {
  visibility: visible;
}

// Only as wide as the play icon
::v-deep .call-recording-cell {
  width: 1%;
  padding-right: 0;
}

// The note column takes the table's spare width, the others keep to their content; rows are nowrap, so the box
// has a floor instead of growing with its text, small enough that the table still fits a 1536px window
::v-deep .call-agent-note-cell {
  width: 100%;
}

.call-agent-note {
  width: 100%;
}

.call-agent-note ::v-deep .agent-note-input {
  width: 100%;
  min-width: 9.5rem;
}
</style>

<style lang="scss">
// The popover is appended to the body, out of reach of the scoped styles. The app's popovers are a fixed 330px,
// too narrow for a call id, so this one grows with its text instead.
.popover.call-id-popover {
  width: auto;
  max-width: none;
}
</style>
